import React, { useMemo, useState } from 'react';
import { Button, Card, Typography, Tag, Space, Result, Descriptions, Alert, message } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { buildChecklist } from './materialConfig';
import { findProject, buildItemStateMap } from './mockProjects';
import CustomChecklist from './CustomChecklist';
import TargetTags from './TargetTags';
import UIDesignPanel from './UIDesignPanel';
import useUIDesign from './useUIDesign';
import { UI_STATUS } from './uiDesignMock';
import useAudit from './useAudit';
import { isAuditApproved, auditStatusInfo } from './auditMock';
import ApplyModal from './ApplyModal';

const { Title, Text } = Typography;

// 企业端项目详情：审核通过后才能填写 / 上传并保存；审核前只读预览；可确认 / 驳回 UI 设计
const CustomDetail = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const location = useLocation();
    const [project, setProject] = useState(() => location.state?.project || findProject(id));
    const [itemStates, setItemStates] = useState(() => (project ? buildItemStateMap(project) : {}));
    const { getState, getStatus, confirm, reject } = useUIDesign();
    const { getStatus: getAuditStatus, getAudit, resubmit } = useAudit();
    const [applyOpen, setApplyOpen] = useState(false);
    const uiTabRequested = location.state?.tab === 'ui';

    const checklist = useMemo(
        () => (project ? buildChecklist(project.targets) : []),
        [project],
    );

    if (!project) {
        return (
            <div style={{ padding: 24 }}>
                <Result status="404" title="未找到该项目" subTitle="项目可能已删除，或链接无效"
                    extra={<Button type="primary" onClick={() => navigate('/app/oem-custom')}>返回我的定制</Button>} />
            </div>
        );
    }

    const handleItemSave = (itemKey, patch) => {
        setItemStates((prev) => ({ ...prev, [itemKey]: { ...prev[itemKey], ...patch } }));
    };

    const uiStatus = getStatus(project.id);
    const uiTag = UI_STATUS[uiStatus];

    const auditStatus = getAuditStatus(project.id);
    const auditInfo = auditStatusInfo(auditStatus);
    const approved = isAuditApproved(auditStatus);
    const lastReject = [...(getAudit(project.id).history || [])].reverse().find((h) => h.action === 'reject');

    const handleResubmit = (values) => {
        setProject((p) => ({ ...p, appName: values.appName, targets: values.targets }));
        resubmit(project.id);
        setApplyOpen(false);
        message.success('已重新提交，等待平台审核');
    };

    return (
        <div style={{ padding: 24 }}>
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Space>
                    <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/app/oem-custom')}>返回</Button>
                    <Title level={4} style={{ margin: 0 }}>{project.appName}</Title>
                    <Tag color="purple" style={{ border: 'none' }}>OEM 定制</Tag>
                    <Tag color={auditInfo.companyColor} style={{ border: 'none' }}>{auditInfo.companyText}</Tag>
                </Space>
                <Text type="secondary" style={{ fontSize: 12 }}>企业端 · 资料提交 / UI 确认</Text>
            </div>

            {auditStatus === 'pending' && (
                <Alert type="info" showIcon style={{ marginBottom: 16 }}
                    message="定制申请审核中"
                    description="平台正在审核你的定制申请，审核通过后即可填写资料；当前可先预览资料清单。" />
            )}
            {auditStatus === 'rejected' && (
                <Alert type="error" showIcon style={{ marginBottom: 16 }}
                    message="定制申请已驳回，请修改后重新提交"
                    description={lastReject?.text || '平台已驳回该定制申请'}
                    action={<Button size="small" type="primary" onClick={() => setApplyOpen(true)}>编辑申请信息</Button>} />
            )}

            <Card bordered={false} style={{ marginBottom: 16, borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                <Descriptions column={3} size="small">
                    <Descriptions.Item label="所属企业">{project.companyName}</Descriptions.Item>
                    <Descriptions.Item label="定制范围"><TargetTags targets={project.targets} /></Descriptions.Item>
                    <Descriptions.Item label="创建时间">{project.createdAt}</Descriptions.Item>
                    <Descriptions.Item label="定制状态"><Tag color={auditInfo.companyColor} style={{ border: 'none' }}>{auditInfo.companyText}</Tag></Descriptions.Item>
                    <Descriptions.Item label="UI设计"><Tag color={uiTag.color} style={{ border: 'none' }}>{uiTag.text}</Tag></Descriptions.Item>
                </Descriptions>
            </Card>

            <Card bordered={false} style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 12 }}>
                    {approved
                        ? '按阶段逐项填写 / 上传资料并保存即可，可随时修改；「UI设计」确认通过后平台才可构建一阶段。'
                        : '审核通过后方可填写 / 上传资料，当前为只读预览；「UI设计」确认通过后平台才可构建一阶段。'}
                </Text>
                <CustomChecklist
                    checklist={checklist}
                    itemStates={itemStates}
                    isService={false}
                    readOnly={!approved}
                    onItemSave={handleItemSave}
                    defaultStage={uiTabRequested ? 'ui' : undefined}
                    uiTab={(
                        <UIDesignPanel
                            state={getState(project.id)}
                            isService={false}
                            onConfirm={() => confirm(project.id)}
                            onReject={(reason) => reject(project.id, reason)}
                        />
                    )}
                />
            </Card>

            <ApplyModal
                open={applyOpen}
                project={project}
                onCancel={() => setApplyOpen(false)}
                onSubmit={handleResubmit}
            />
        </div>
    );
};

export default CustomDetail;
